import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { postData } from "../../../../utility/Utility";
import CustomLoader from "../../../../Components/CustomLoader/CustomLoader";

function HeroSliderList() {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchSlides = async (page = 1, search = "") => {
    setLoading(true);
    try {
      // Call the API to get all hero slides
      const response = await postData(`api/admin/hero-slides/get-all`);
     
      
      // Check if response is an array (direct data) or has success/data structure
      let slidesData = [];
      
      if (Array.isArray(response)) {
        // Direct array response
        slidesData = response;
      } else if (response && response.success && response.data) {
        // Success/data structure
        slidesData = response.data;
      } else if (response && response.data && Array.isArray(response.data)) {
        // Just data property with array
        slidesData = response.data;
      }
      
      // Filter based on search term if provided
      let filteredSlides = [...slidesData];
      if (search) {
        filteredSlides = filteredSlides.filter(slide => 
          (slide.title && slide.title.toLowerCase().includes(search.toLowerCase())) ||
          (slide.subtitle && slide.subtitle.toLowerCase().includes(search.toLowerCase())) ||
          (slide.description && slide.description.toLowerCase().includes(search.toLowerCase()))
        );
      }
      
      // Paginate results
      const startIndex = (page - 1) * rowsPerPage;
      const paginatedSlides = filteredSlides.slice(startIndex, startIndex + rowsPerPage);
      
      setSlides(paginatedSlides);
      setTotalPages(Math.ceil(filteredSlides.length / rowsPerPage));
    } catch (error) {
      toast.error("Error fetching slides");
      console.error("Fetch slides error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchSlides(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchSlides(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        
        
        // Call the API to delete the slide
        const response = await postData(`api/admin/hero-slides/delete/${id}`);
        
        
        
        // Handle different response formats
        if (response && (response.success || (response.data && response.data._id) || response._id)) {
          toast.success(response.message || "Slide deleted successfully");
          // Refresh the slides list
          fetchSlides(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete slide");
        }
      } catch (error) {
        toast.error("Error deleting slide");
        console.error("Delete slide error:", error);
      }
    }
  };

  const handleStatusChange = async (slide) => {
    // Determine the new status (opposite of current status)
    const isCurrentlyActive = slide.status === "active" || slide.isActive === true;
    const newStatus = !isCurrentlyActive;

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus ? "activate" : "deactivate"} this slide?`,
      text: `The slide will be ${newStatus ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {
        // Prepare the data for the update API call
        const updateData = {
          title: slide.title,
          subtitle: slide.subtitle,
          description: slide.description,
          isActive: newStatus,
          image: slide.image // Include the image in the update
        };
        
        
        
        // Call the API to update the slide status
        const response = await postData(`api/admin/hero-slides/update/${slide._id}`, updateData);
        
       
        
        // Handle different response formats
        if (response && (response.success || (response.data && response.data._id) || response._id)) {
          toast.success(response.message || `Slide ${newStatus ? "activated" : "deactivated"} successfully`);
          // Refresh the slides list
          fetchSlides(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || `Failed to ${newStatus ? "activate" : "deactivate"} slide`);
        }
      } catch (error) {
        toast.error(`Error ${newStatus ? "activating" : "deactivating"} slide`);
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={3000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Hero Slider Management</h4>
              <p className="fw-semibold my-2">Hero Slider List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-hero-slide")}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                Add Slide
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search slides..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Title</th>
                            <th>Subtitle</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slides.length > 0 ? (
                            slides.map((slide, index) => (
                              <tr key={slide._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  <img 
                                    src={slide.image} 
                                    alt={slide.title} 
                                    style={{ width: '80px', height: '50px', objectFit: 'cover' }}
                                  />
                                </td>
                                <td>{slide.title}</td>
                                <td>{slide.subtitle}</td>
                                <td>{slide.description}</td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${(slide.status === 'active' || slide.isActive === true) ? 'success' : 'danger'}`}
                                  >
                                    {(slide.status === 'active' || slide.isActive === true) ? 'active' : 'inactive'}
                                  </span>
                                </td>
                                <td>
                                  {new Date(slide.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-hero-slide`, {
                                          state: { slideData: slide },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${(slide.status === 'active' || slide.isActive === true) ? 'warning' : 'success'}`}
                                      onClick={() => handleStatusChange(slide)}
                                      title={(slide.status === 'active' || slide.isActive === true) ? 'Deactivate' : 'Activate'}
                                    >
                                      <i className={`mdi mdi-${(slide.status === 'active' || slide.isActive === true) ? 'close' : 'check'}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(slide._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" className="text-center py-4">
                                No slides found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HeroSliderList;